// iter195 — P&L Financial Drill-Down.
//
// Right-side drawer that explains any single number on the iter192
// P&L donut. `bucket` selects which category:
//   - "revenue" → client payments (payment_milestones)
//   - "payroll" → paid payroll (payroll_payments where paid=True)
//   - "office"  → office debits (office_expenses transaction_type=debit)
//   - "net"     → summary tile with links to the three above.
//
// Backend `/dashboard/pnl-breakdown` reuses the same filters as
// `/dashboard/pnl`, so the drawer total ALWAYS reconciles with the
// donut. Zero double-counting risk.
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/utils";
import {
  X, Search, Receipt, Users, Building2, ArrowRight,
  TrendingUp, TrendingDown, Loader2,
} from "lucide-react";

const BUCKET_META = {
  revenue: {
    title: "Client Revenue",
    icon: Receipt,
    iconClass: "text-emerald-700",
    headerBg: "bg-emerald-50/40",
    footerBg: "bg-emerald-50/60",
    totalClass: "text-emerald-800",
    labelClass: "text-emerald-800",
    empty: "No client payments recorded in this period yet.",
  },
  payroll: {
    title: "Payroll Paid",
    icon: Users,
    iconClass: "text-amber-700",
    headerBg: "bg-amber-50/40",
    footerBg: "bg-amber-50/60",
    totalClass: "text-amber-800",
    labelClass: "text-amber-800",
    empty: "No payroll payments in this period yet.",
  },
  office: {
    title: "Office Expenses",
    icon: Building2,
    iconClass: "text-rose-700",
    headerBg: "bg-rose-50/40",
    footerBg: "bg-rose-50/60",
    totalClass: "text-rose-800",
    labelClass: "text-rose-800",
    empty: "No office expenses in this period yet.",
  },
};

const MODE_LABELS = {
  cash: "Cash", upi: "UPI", bank: "Bank Transfer",
  cheque: "Cheque", card: "Card", other: "Other",
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ---------------------------------------------------------------------
// Row renderers per bucket — kept declarative so the drawer body is
// tiny.
// ---------------------------------------------------------------------
function RevenueRow({ r }) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2 items-baseline py-2.5 border-b border-stone-100 last:border-0"
         data-testid={`pnl-drill-row-${r.id}`}>
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-sm text-stone-800 truncate">
            {r.client_name || "Unnamed client"}
          </span>
          {r.event && (
            <span className="text-[10px] text-stone-400 uppercase tracking-wider">· {r.event}</span>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5 flex items-center gap-2">
          <span>{fmtDate(r.date)}</span>
          {r.mode && <span className="text-stone-400">· {MODE_LABELS[r.mode] || r.mode}</span>}
          {r.reference && <span className="text-stone-400 truncate">· Ref {r.reference}</span>}
        </div>
        {r.note && (
          <div className="text-[11px] text-stone-500 italic mt-0.5 truncate">{r.note}</div>
        )}
      </div>
      <div className="font-mono text-sm font-semibold tabular-nums text-emerald-700 shrink-0">
        +{inr(r.amount)}
      </div>
    </div>
  );
}

function PayrollRow({ r }) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2 items-baseline py-2.5 border-b border-stone-100 last:border-0"
         data-testid={`pnl-drill-row-${r.id}`}>
      <div className="min-w-0">
        <div className="font-medium text-sm text-stone-800 truncate">
          {r.staff_name || "Unnamed staff"}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5 flex items-center gap-2">
          <span>{fmtDate(r.date)}</span>
          {r.reference && <span className="text-stone-400 truncate">· Ref {r.reference}</span>}
        </div>
        {r.note && (
          <div className="text-[11px] text-stone-500 italic mt-0.5 truncate">{r.note}</div>
        )}
      </div>
      <div className="font-mono text-sm font-semibold tabular-nums text-amber-700 shrink-0">
        {inr(r.amount)}
      </div>
    </div>
  );
}

function OfficeRow({ r }) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2 items-baseline py-2.5 border-b border-stone-100 last:border-0"
         data-testid={`pnl-drill-row-${r.id}`}>
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-sm text-stone-800 truncate">
            {r.description || r.category || "Expense"}
          </span>
          {r.category && r.description && (
            <span className="text-[10px] text-stone-400 uppercase tracking-wider">· {r.category}</span>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5 flex items-center gap-2">
          <span>{fmtDate(r.date)}</span>
          {r.mode && <span className="text-stone-400">· {MODE_LABELS[r.mode] || r.mode}</span>}
          {r.reference && <span className="text-stone-400 truncate">· Ref {r.reference}</span>}
        </div>
        {r.note && (
          <div className="text-[11px] text-stone-500 italic mt-0.5 truncate">{r.note}</div>
        )}
      </div>
      <div className="font-mono text-sm font-semibold tabular-nums text-rose-700 shrink-0">
        {inr(r.amount)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Net summary tile — shows the math AND lets the user drill further.
// ---------------------------------------------------------------------
function NetSummary({ period, periodLabel, onOpenBucket }) {
  const [pnl, setPnl] = useState(null);
  useEffect(() => {
    api.get(`/dashboard/pnl?period=${period}`).then((r) => setPnl(r.data)).catch(() => {});
  }, [period]);
  if (!pnl) {
    return (
      <div className="p-6 text-center text-xs text-stone-400 italic">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
        Loading…
      </div>
    );
  }
  const isProfit = pnl.net >= 0;
  const Tile = ({ label, amount, bucket, icon: Icon, tileIcon, tileAmt }) => (
    <button
      type="button"
      className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg border border-stone-200 bg-white hover:border-stone-400 hover:shadow-sm transition group"
      onClick={() => onOpenBucket(bucket)}
      data-testid={`pnl-drill-net-open-${bucket}`}
    >
      <div className="flex items-center gap-3">
        <Icon className={`w-4 h-4 ${tileIcon}`} strokeWidth={1.8} />
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500 font-semibold">
            {label}
          </div>
          <div className="text-[9px] uppercase tracking-widest text-stone-400 mt-0.5 group-hover:text-stone-600">
            View transactions →
          </div>
        </div>
      </div>
      <div className={`font-mono text-sm font-semibold tabular-nums ${tileAmt}`}>
        {inr(amount)}
      </div>
    </button>
  );
  return (
    <div className="p-4 space-y-3">
      <Tile label="Client Revenue" amount={pnl.revenue.total} bucket="revenue"
            icon={Receipt} tileIcon="text-emerald-700" tileAmt="text-emerald-700" />
      <Tile label="Payroll Paid" amount={pnl.expenses.payroll.total} bucket="payroll"
            icon={Users} tileIcon="text-amber-700" tileAmt="text-amber-700" />
      <Tile label="Office Expenses" amount={pnl.expenses.office.total} bucket="office"
            icon={Building2} tileIcon="text-rose-700" tileAmt="text-rose-700" />
      <div className="px-4 py-4 rounded-lg mt-4 border-t-2 border-stone-800 bg-stone-50/60"
           data-testid="pnl-drill-net-summary">
        <div className="flex items-baseline justify-between">
          <div className="flex items-center gap-2">
            {isProfit ? <TrendingUp className="w-4 h-4 text-emerald-700" strokeWidth={2} />
                      : <TrendingDown className="w-4 h-4 text-rose-700" strokeWidth={2} />}
            <span className={`text-[11px] uppercase tracking-[0.14em] font-semibold ${
              isProfit ? "text-emerald-800" : "text-rose-800"
            }`}>
              {isProfit ? "Net Profit" : "Net Loss"} · {periodLabel}
            </span>
          </div>
          <div className={`font-mono text-lg font-semibold tabular-nums ${
            isProfit ? "text-emerald-800" : "text-rose-800"
          }`}>
            {isProfit ? "+" : "−"}{inr(Math.abs(pnl.net))}
          </div>
        </div>
        <div className="mt-2 text-[10px] uppercase tracking-widest text-stone-500">
          Revenue − (Payroll + Office) = Net
        </div>
      </div>
    </div>
  );
}


export default function PnLDrillDrawer({ open, bucket, period, periodLabel, onClose, onSwitchBucket }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open || !bucket || bucket === "net") { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/dashboard/pnl-breakdown?period=${period}&bucket=${bucket}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, bucket, period]);

  useEffect(() => {
    if (!open) setQuery("");
    const onKey = (e) => { if (e.key === "Escape" && open) onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const meta = BUCKET_META[bucket];
  const Icon = meta?.icon || Receipt;

  const filtered = useMemo(() => {
    if (!data?.rows) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) => {
      const fields = [
        r.client_name, r.staff_name, r.description, r.category,
        r.reference, r.note, r.mode, r.event,
      ].filter(Boolean).join(" ").toLowerCase();
      return fields.includes(q);
    });
  }, [data, query]);

  if (!open) return null;

  const isNet = bucket === "net";
  const RowComponent = bucket === "revenue" ? RevenueRow
    : bucket === "payroll" ? PayrollRow
    : bucket === "office"  ? OfficeRow
    : null;
  const showSearch = data?.rows?.length > 6;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40 backdrop-blur-sm"
         onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white w-full sm:max-w-md h-full shadow-2xl overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}
           data-testid="pnl-drill-drawer">
        {/* Header */}
        <div className={`px-5 py-4 border-b border-stone-200 flex items-start justify-between ${
          isNet ? "" : (meta?.headerBg || "")
        }`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <Icon className={`w-4 h-4 shrink-0 ${
              isNet ? "text-stone-700" : (meta?.iconClass || "")
            }`} strokeWidth={2} />
            <div className="min-w-0">
              <div className="font-heading font-semibold text-base truncate">
                {isNet ? "Net Profit / Loss Breakdown" : meta?.title}
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500 mt-0.5">
                {periodLabel}{data ? ` · ${data.count} ${data.count === 1 ? "entry" : "entries"}` : ""}
              </div>
            </div>
          </div>
          <button className="btn-ghost p-1 rounded hover:bg-stone-100"
                  onClick={onClose} aria-label="Close" data-testid="pnl-drill-close">
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isNet ? (
            <NetSummary period={period} periodLabel={periodLabel} onOpenBucket={onSwitchBucket} />
          ) : (
            <>
              {showSearch && (
                <div className="px-5 pt-4 pb-2">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input type="text" className="input pl-8 py-1.5 text-sm"
                           placeholder={`Search ${meta?.title.toLowerCase()}…`}
                           value={query} onChange={(e) => setQuery(e.target.value)}
                           data-testid="pnl-drill-search" />
                  </div>
                </div>
              )}
              <div className="px-5 py-2">
                {loading ? (
                  <div className="py-10 text-center text-xs text-stone-400">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                    Loading transactions…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="py-10 text-center text-xs text-stone-400 italic"
                       data-testid="pnl-drill-empty">
                    {query ? "No transactions match your search." : meta?.empty}
                  </div>
                ) : (
                  <div>
                    {filtered.map((r) => <RowComponent key={r.id} r={r} />)}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer total */}
        {!isNet && data && (
          <div className={`px-5 py-3.5 border-t border-stone-200 flex items-center justify-between ${
            meta?.footerBg || ""
          }`} data-testid="pnl-drill-footer">
            <div className={`text-[11px] uppercase tracking-[0.14em] font-semibold ${meta?.labelClass || ""}`}>
              Total {meta?.title.toLowerCase()}
            </div>
            <div className={`font-mono text-base font-semibold tabular-nums ${meta?.totalClass || ""}`}
                 data-testid="pnl-drill-total">
              {bucket === "revenue" ? "+" : ""}{inr(data.total)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
